/* eslint-disable complexity */
/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-lines */
import { Spacing } from '@vkontakte/vkui';
import classNames from 'clsx';
import { xray } from 'lib/xray';
import throttle from 'lodash.throttle';
import React, { type ReactElement, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_TABLET_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import {
    crossedFullPriceInTariffCard,
    isRebrandingQuotaLanding,
    moreBenefitsInExpensiveTariffs,
    preferredTariffs,
    sendStartEventTimer,
    yearlyPriceOnMonthlyTariffButtonTouch,
} from 'reactApp/appHelpers/featuresHelpers';
import { cheapestTariff } from 'reactApp/appHelpers/featuresHelpers/features/cheapestTariff';
import { compactFirstScroll } from 'reactApp/appHelpers/featuresHelpers/features/compactFirstScroll';
import { enabledFamilyTariff } from 'reactApp/appHelpers/featuresHelpers/features/familyTariff';
import { favorableTariffs } from 'reactApp/appHelpers/featuresHelpers/features/favorableTariffs';
import { isPromoQuotaWithLitress } from 'reactApp/appHelpers/featuresHelpers/features/litres';
import { mayPromo2025YearProductId } from 'reactApp/appHelpers/featuresHelpers/features/mayPromo2025';
import { promocodeInTariffsMobile } from 'reactApp/appHelpers/featuresHelpers/features/promocodeOnPromoQuota';
import { quotaLandingLoadProductsTimeout } from 'reactApp/appHelpers/featuresHelpers/features/quotaLandingLoadProductsTimeouts';
import { tariffPollDelay } from 'reactApp/appHelpers/featuresHelpers/features/tariffPoll';
import { yearPriceInMonthEnabled } from 'reactApp/appHelpers/featuresHelpers/features/yearPriceInMonth';
import { START_EVENT_TIMER_MS } from 'reactApp/constants';
import { useMinWidthBreakpoint } from 'reactApp/hooks/responsiveness/useMinWidthBreakpoint';
import { MediaSelectors } from 'reactApp/modules/media/media.selectors';
import { loadPaidInfoRequest } from 'reactApp/modules/paidInfo/paidInfo.module';
import { PaidInfoSelectors } from 'reactApp/modules/paidInfo/paidInfo.selectors';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { loadQuotaLandingProducts, showLandingTariffsAction } from 'reactApp/modules/quotaLanding/quotaLanding.module';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { ReferralProgramSelectors } from 'reactApp/modules/referralProgram/referralProgram.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserStorageActions } from 'reactApp/modules/user/userStorage';
import { TariffCard } from 'reactApp/sections/MobileSubscriptionsPage/TariffCard/TariffCard';
import { MobileTariffCardTheme } from 'reactApp/sections/MobileSubscriptionsPage/TariffCard/TariffCard.types';
import { Container } from 'reactApp/sections/QuotaLanding/Container/Container';
import { useFamilyTariffFromQuery } from 'reactApp/sections/QuotaLanding/hooks/useFamilyTariffFromQuery';
import { useIntersecObsrWithParams } from 'reactApp/sections/QuotaLanding/hooks/useIntersecObsrWithParams';
import { openPromocodeModal, sendPromocodeButtonAnalytics } from 'reactApp/sections/QuotaLanding/PromocodeDialog/PromocodeDialog.helpers';
import { ETabs } from 'reactApp/sections/QuotaLanding/QuotaLanding.data';
import { sendQuotaBlockViewDWH, sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { renderTariffPollModal, TARIFF_POLL_MODAL_ID } from 'reactApp/sections/QuotaLanding/TariffPollDialog/TariffPollDialog.helpers';
import { useGetTariffList } from 'reactApp/sections/QuotaLanding/TariffsList/hook/useGetTariffList';
import { TariffPeriodSwitch } from 'reactApp/sections/QuotaLanding/TariffsList/TariffPeriodSwitch/TariffPeriodSwitch';
import { useMobileBuyClick } from 'reactApp/sections/QuotaLanding/TariffsList/useMobileBuyClick';
import { TariffPlansCard } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/TariffPlansCard';
import { useBuyClick } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/useBuyClick';
import { getMaxTariffDiscount } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffsPlans.selectors';
import type { RootState } from 'reactApp/store';
import type { SwiperNavigator } from 'reactApp/types/SwiperNavigator';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { DiscountLabel } from 'reactApp/ui/DiscountLabel/DiscountLabel';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import { sendTmrGoal, TMR3473039 } from 'reactApp/utils/tmr';
import { Mousewheel } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { MobileTariffCardLoader } from './MobileTariffCardLoader/MobileTariffCardLoader';
import { NavButtons } from './NavButtons/NavButtons';
import { TariffCardLoader } from './TariffCardLoader/TariffCardLoader';
import styles from './TariffsList.css';

interface TariffsPlansProps {
    changeTab?: (tab) => void;
    isIOSPromo?: boolean;
    isAndroidPromo?: boolean;
    setCarouselLoaded?: (state: boolean) => void;
    headerHeight?: number;
    isMobilePayAvailable: boolean;
    isMobilePayFromGetParam: boolean;
    isAnonymous: boolean;
    isTariffsPage?: boolean;
    isPhone?: boolean;
    activeInitTab?: ETabsName;
    minTariff?: number;
    mayPromo2025Enabled?: boolean;
}

const ACTION_PROMO_MAX_DISCOUNT = 80;

export const TariffsList = forwardRef<HTMLDivElement | null, TariffsPlansProps>(
    // eslint-disable-next-line max-lines-per-function
    (
        {
            isAnonymous,
            changeTab,
            isIOSPromo,
            isAndroidPromo,
            setCarouselLoaded,
            headerHeight = 0,
            isMobilePayAvailable,
            isMobilePayFromGetParam,
            isTariffsPage,
            isPhone = false,
            activeInitTab,
            minTariff,
            mayPromo2025Enabled,
        },
        ref
    ): ReactElement => {
        const dispatch = useDispatch();

        const isMobileSelector = useSelector(QuotaLandingSelectors.isMobile);
        const isMobile = isPhone || isMobileSelector;
        const isTablet = useSelector(MediaSelectors.isTablet);
        // tempexp_18086-next-line
        const isReferralLanding = useSelector(ReferralProgramSelectors.getRefId);
        const isPaid = useSelector(UserSelectors.isPaidUser);
        const isAlmostFullFreeUser = useSelector(UserSelectors.isAlmostFullFreeUser);
        const maxTariffDiscount = useSelector((state: RootState) => getMaxTariffDiscount(state, isMobile));
        let discount = maxTariffDiscount;
        // tempexp_16373-next-line
        const isFavorableTariffs = favorableTariffs.length;

        const forceFamilyTariffFromQuery = useFamilyTariffFromQuery();

        // tempexp_16373-next-line
        if (!isFavorableTariffs) {
            discount = ACTION_PROMO_MAX_DISCOUNT;
        }

        const [activeTab, setActiveTab] = useState<ETabsName>(activeInitTab || ETabsName.year);

        const tariffList = useGetTariffList({
            isMobile,
            minTariff,
            forceFamilyTariffFromQuery,
        });
        const { isLoading, isLoaded } = useSelector(ProductsSelectors.getLifeCycleState);
        const firstLoad = useRef<boolean>(false);

        const [navState, setNavState] = useState({ isStart: true, isLast: false });
        const [navigator, setNavigator] = useState<SwiperNavigator | null>(null);

        // tempexp_18090-start
        const tariffPollTimer = useRef<number | undefined>();
        // todo: заменить на новую ручку billinginfo
        const userWithoutPayment = useSelector(PaidInfoSelectors.isUserWithoutPayment);
        const { isLoaded: isPaidInfoLoaded } = useSelector(PaidInfoSelectors.getLoadingState);
        // tempexp_18090-end

        const productsLoadTimer = useRef<NodeJS.Timeout | null>(null);

        const onBuyClick = useBuyClick({
            isMobilePayAvailable,
            isMobilePayFromGetParam,
            isIOSPromo,
            isAndroidPromo,
            enabledMidasPayment: true,
        });

        const onMobileBuyClick = useMobileBuyClick({
            mayPromo2025Enabled,
            isTariffsPage,
            onBuyClick,
        });

        const updateNavState = useCallback(() => {
            setNavState({ isStart: navigator?.isFirst() as boolean, isLast: navigator?.isLast() as boolean });
        }, [navigator, setNavState]);

        const onClickSwitch = useCallback((tab) => {
            sendQuotaGa({ action: 'change', label: 'period', name_button: tab });
            sendTmrGoal({ id: TMR3473039, goal: 'change-period' });
            setActiveTab(tab);
        }, []);

        const handleSetNavigator = useCallback(
            (navigator) => {
                setNavigator(navigator);
            },
            [setNavigator, updateNavState]
        );

        // отправляем событие с периодом из фичи или раз в 10 минут
        const sendStartViewEvent = throttle(() => {
            dispatch(showLandingTariffsAction());
        }, sendStartEventTimer || START_EVENT_TIMER_MS);

        const onIntersecting = useCallback(() => {
            if (sendStartEventTimer) {
                sendStartViewEvent();
            }

            emitAnalyticEvent(AnalyticEventNames.TARIFFS_SHOWN, { isAlmostFullFreeUser });
            sendQuotaGa({
                action: 'tariff',
                label: 'view',
                multi: false,
                dwh_enabled: true,
                dwh_enabled_fields: [['multi', 'tariffs_line', 'platform']],
            });
            sendTmrGoal({ id: TMR3473039, goal: 'tariff-view' });
            sendQuotaBlockViewDWH('tariffs');
            changeTab?.(ETabs.tariffs);

            if (isMobile) {
                sendPromocodeButtonAnalytics('show', 'tariffs', 'touch');
            }

            /**
             * Загружаем продукты если юзера доскролил до блока с тарифами и остался
             * на нем какое-то время (quotaLandingLoadProductsTimeout)
             */
            if (!isLoaded && !isLoading && !productsLoadTimer?.current && !isTariffsPage && quotaLandingLoadProductsTimeout && !isMobile) {
                const id = setTimeout(() => {
                    xray.send('quota_tariffs_timeout_load');
                    dispatch(loadQuotaLandingProducts());
                }, quotaLandingLoadProductsTimeout);

                productsLoadTimer.current = id;
            }
        }, [changeTab, isLoaded, isLoading, dispatch, isTariffsPage, isMobile, isAlmostFullFreeUser]);

        const onIntersectingOut = useCallback(() => {
            if (productsLoadTimer?.current) {
                /**
                 * Сбрасываем таймер загрузки продуктов
                 * Нужно чтобы продукты не грузились когда происходит быстрый якорный скролл.
                 */
                clearTimeout(productsLoadTimer.current);
                productsLoadTimer.current = null;
            }
        }, []);

        const refIntersection = useIntersecObsrWithParams({
            onIntersecting,
            onIntersectingOut,
            isMobile,
            headerHeight,
        });

        useEffect(() => {
            xray.send('quota_show_tariffs_list');

            if (Boolean(quotaLandingLoadProductsTimeout) && !isTariffsPage) {
                xray.send(`quota_tariffs_timeout_init${isMobile ? '_mobile' : ''}`);
            }

            return () => {
                if (productsLoadTimer?.current) {
                    clearTimeout(productsLoadTimer.current);
                    productsLoadTimer.current = null;
                }
            };
        }, []);

        useEffect(() => {
            // tempexp_18090-start
            if (!isPaid && !isPaidInfoLoaded && !isAnonymous) {
                dispatch(loadPaidInfoRequest());
            }

            if (!isPaid && isPaidInfoLoaded && userWithoutPayment && !tariffPollTimer?.current && tariffPollDelay) {
                const id = window.setTimeout(() => {
                    const tariffPollShown = Boolean(dispatch(UserStorageActions.get(TARIFF_POLL_MODAL_ID)));
                    if (!tariffPollShown) {
                        renderTariffPollModal();
                    }
                }, tariffPollDelay);

                tariffPollTimer.current = id;
            }

            return () => {
                if (tariffPollTimer?.current) {
                    window.clearInterval(tariffPollTimer.current);
                }
            };
            // tempexp_18090-end
        }, [userWithoutPayment, isPaidInfoLoaded, isAnonymous, dispatch]);

        useEffect(() => {
            if (isLoaded && !firstLoad.current) {
                firstLoad.current = true;
            }

            if (isLoaded && !isTariffsPage && quotaLandingLoadProductsTimeout) {
                xray.send(`quota_tariffs_timeout_show${isMobile ? '_mobile' : ''}`);
            }
        }, [isLoaded]);

        useEffect(() => {
            updateNavState();
        }, [navigator, updateNavState, tariffList?.length]);

        const cardRef = useRef<HTMLDivElement>(null);
        /**
         * Под анонимом список тарифов захардкожен (в онлайнконфе) и сразу имеет длину (не загружается из стора)
         * При этом рефка на начальной загрузке еще не установлена
         * из-за этого происходит не верный рассчет переменной на начальной загрузке и получается 4 * (0 + 24) = 96
         *
         * Если аноним и рефка еще не установлена, устанавливаем переменную в 0. Как только рефка загрузится, произойдет перерасчет.
         */
        const lgBreackPoint = cardRef?.current
            ? tariffList.length * ((cardRef.current?.scrollWidth ?? 0) + (isRebrandingQuotaLanding ? 24 : 16))
            : 0;
        const [lgBreakpointHit, breackPoint, setBreackPoint] = useMinWidthBreakpoint(lgBreackPoint);
        useEffect(() => {
            if (!breackPoint) {
                setBreackPoint(lgBreackPoint);
            }
        }, [lgBreackPoint]);
        const displayInSwiper = !isFavorableTariffs || !lgBreakpointHit;

        /* tempexp_16800-start */
        const calcCardStyles = (style: string, isSwiperSlot?: boolean) =>
            classNames(style, {
                [styles.tariffCard_large]: moreBenefitsInExpensiveTariffs === 'c' || moreBenefitsInExpensiveTariffs === 'd',
                [isSwiperSlot ? styles.slide_rebranding : styles.favorableTariffCard_rebranding]: isRebrandingQuotaLanding,
            });
        /* tempexp_16800-end */

        // tempexp_18090-start
        const onTariffClick = useCallback(() => {
            if (tariffPollDelay && tariffPollTimer?.current) {
                window.clearInterval(tariffPollTimer.current);
            }
        }, [tariffPollTimer]);
        // tempexp_18090-end

        const handlePromocode = useCallback(() => {
            openPromocodeModal();
            sendPromocodeButtonAnalytics('click', 'tariffs', 'touch');
        }, []);

        if (isMobile) {
            return (
                <div ref={refIntersection}>
                    <div ref={ref}>
                        <Container isNarrow>
                            <div className={styles.mobileList}>
                                {tariffList.map((tariff, index) => (
                                    <div
                                        className={classNames(styles.mobileTariff, {
                                            [styles.mobileTariff_rebranding]: isRebrandingQuotaLanding,
                                        })}
                                        key={tariff.products?.[0].id}
                                    >
                                        <TariffCard
                                            {...tariff}
                                            onClick={onMobileBuyClick}
                                            /* tempexp_17509-next-line */
                                            isTopCard={index === 0}
                                            openAbCard={index === 0 || isTablet || IS_TABLET_BROWSER}
                                            alwaysExpand={isTablet || IS_TABLET_BROWSER}
                                            theme={isTariffsPage ? MobileTariffCardTheme.grey : MobileTariffCardTheme.white}
                                            isSingleQuota
                                            showPriceByMonth={
                                                yearlyPriceOnMonthlyTariffButtonTouch === 'c' || yearPriceInMonthEnabled === 'b'
                                            }
                                            skipBenefitsFeature={isTariffsPage}
                                            setCarouselLoaded={setCarouselLoaded}
                                            crossFullPrice={crossedFullPriceInTariffCard}
                                            // tempexp_17020-start
                                            showPriceByYear={yearlyPriceOnMonthlyTariffButtonTouch === 'b'}
                                            // tempexp_17020-start
                                            skipDiscountPrice={Boolean(preferredTariffs.length)}
                                            isRebrandingQuotaLanding={isRebrandingQuotaLanding}
                                            // tempexp_17509-next-line
                                            isFromLitres={isPromoQuotaWithLitress && !isPaid}
                                            // tempexp_18086-next-line
                                            isReferral={Boolean(isReferralLanding)}
                                            isFamily={Boolean(enabledFamilyTariff && tariff?.isFamily)}
                                            isMayPromo2025Tariff={
                                                mayPromo2025Enabled && tariff.products[1]?.id === mayPromo2025YearProductId
                                            }
                                            mayPromo2025Enabled={mayPromo2025Enabled}
                                            isDeletingDuplicates
                                        />
                                    </div>
                                ))}
                                {(isLoading || !isLoaded) &&
                                    !firstLoad.current &&
                                    Array.from(new Array(6)).map((_, idx) => (
                                        <div className={styles.mobileTariff} key={`mobileCard_${idx}`}>
                                            <MobileTariffCardLoader
                                                isExpand={idx === 0 || Boolean(isTablet)}
                                                isDark={isTariffsPage}
                                                isTariffsPage={isTariffsPage}
                                                headerHeight={headerHeight}
                                            />
                                        </div>
                                    ))}
                            </div>
                            {promocodeInTariffsMobile && (
                                <>
                                    <Spacing size={32} />
                                    <Button
                                        theme="vk"
                                        sizeMode={ButtonSizeMode.extraBig}
                                        className={styles.buttonPromocode}
                                        onClick={handlePromocode}
                                    >
                                        Ввести промокод
                                    </Button>
                                </>
                            )}
                        </Container>
                    </div>
                </div>
            );
        }

        let desktopLoadersCount = favorableTariffs.length;
        if (cheapestTariff !== 'a') {
            desktopLoadersCount++;
        }

        return (
            <div ref={refIntersection}>
                <div className={classNames(styles.root, { [styles.root_anonymous]: isAnonymous })} ref={ref}>
                    <div className={styles.header}>
                        <div className={styles.switch}>
                            {Boolean(discount) && (
                                <div
                                    className={classNames(styles.discountLabel, {
                                        [styles.discountLabel_compact]: compactFirstScroll,
                                        // tempexp_18086-next-line
                                        [styles.discountLabel_referral]: Boolean(isReferralLanding),
                                    })}
                                >
                                    <DiscountLabel
                                        discount={discount}
                                        isAction
                                        // tempexp_18086-next-line
                                        isReferral={Boolean(isReferralLanding)}
                                        isRebrandingQuotaLanding={isRebrandingQuotaLanding}
                                        // tempexp_17509-start
                                        isFromLitres={isPromoQuotaWithLitress && !isPaid}
                                        // tempexp_17509-end
                                    />
                                </div>
                            )}
                            <TariffPeriodSwitch
                                checkedOption={activeTab}
                                onClickSwitch={onClickSwitch}
                                // tempexp_18086-next-line
                                isReferral={Boolean(isReferralLanding)}
                            />
                        </div>
                        {/* tempexp_16373-next-line */}
                        {displayInSwiper && (
                            <div className={styles.navButtons}>
                                <NavButtons
                                    isLast={navState.isLast}
                                    isStart={navState.isStart}
                                    onNext={navigator?.navigateToNext}
                                    onPrev={navigator?.navigateToPrev}
                                    updateNavState={updateNavState}
                                />
                            </div>
                        )}
                    </div>

                    {/* tempexp_16373-next-line */}
                    {!displayInSwiper ? (
                        <div
                            className={classNames(styles.favorableTariffsList, {
                                [styles.favorableTariffsList_rebranding]: isRebrandingQuotaLanding,
                            })}
                        >
                            {tariffList.map((tariff, idx) => (
                                <div ref={cardRef} className={calcCardStyles(styles.favorableTariffCard)} key={idx}>
                                    <TariffPlansCard
                                        // tempexp_18086-next-line
                                        isReferral={Boolean(isReferralLanding)}
                                        activeTab={activeTab}
                                        tariff={tariff}
                                        isIOSPromo={isIOSPromo}
                                        isAndroidPromo={isAndroidPromo}
                                        isMobilePayAvailable={isMobilePayAvailable}
                                        isMobilePayFromGetParam={isMobilePayFromGetParam}
                                        setCarouselLoaded={setCarouselLoaded}
                                        // tempexp_18090-next-line
                                        onTariffClick={onTariffClick}
                                        mayPromo2025Enabled={mayPromo2025Enabled}
                                    />
                                </div>
                            ))}
                            {(isLoading || !isLoaded) &&
                                !firstLoad.current &&
                                Array.from(new Array(desktopLoadersCount)).map((_, idx) => (
                                    <div ref={cardRef} className={calcCardStyles(styles.favorableTariffCard)} key={`tariffCard_${idx}`}>
                                        <TariffCardLoader />
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <div className={classNames(styles.list, { [styles.list_rebranding]: isRebrandingQuotaLanding })}>
                            <Swiper
                                className={enabledFamilyTariff || forceFamilyTariffFromQuery ? 'swiper-padding' : ''}
                                slidesPerView={'auto'}
                                spaceBetween={isRebrandingQuotaLanding ? 24 : 16}
                                threshold={20}
                                pagination={{
                                    clickable: true,
                                }}
                                onSwiper={(swiper) => {
                                    handleSetNavigator({
                                        navigateToNext: () => swiper?.slideTo(swiper?.activeIndex + 2),
                                        navigateToPrev: () => swiper?.slideTo(swiper?.activeIndex - 2),
                                        isFirst: () => swiper?.isBeginning,
                                        isLast: () => swiper?.isEnd,
                                    });
                                }}
                                onActiveIndexChange={updateNavState}
                                direction="horizontal"
                                mousewheel={{
                                    forceToAxis: true,
                                    sensitivity: 2,
                                }}
                                modules={[Mousewheel]}
                            >
                                {tariffList.map((tariff, idx) => (
                                    <SwiperSlide key={tariff.tariffId} virtualIndex={idx} className={calcCardStyles(styles.slide, true)}>
                                        {() => {
                                            return (
                                                <div ref={cardRef} className={calcCardStyles(styles.carouselCard, true)}>
                                                    <TariffPlansCard
                                                        // tempexp_18086-next-line
                                                        isReferral={Boolean(isReferralLanding)}
                                                        activeTab={activeTab}
                                                        tariff={tariff}
                                                        isIOSPromo={isIOSPromo}
                                                        isAndroidPromo={isAndroidPromo}
                                                        isMobilePayAvailable={isMobilePayAvailable}
                                                        isMobilePayFromGetParam={isMobilePayFromGetParam}
                                                        setCarouselLoaded={setCarouselLoaded}
                                                        // tempexp_18090-next-line
                                                        onTariffClick={onTariffClick}
                                                        mayPromo2025Enabled={mayPromo2025Enabled}
                                                    />
                                                </div>
                                            );
                                        }}
                                    </SwiperSlide>
                                ))}
                                {(isLoading || !isLoaded) &&
                                    !firstLoad.current &&
                                    Array.from(new Array(6)).map((_, idx) => (
                                        <SwiperSlide
                                            key={`tariffCard_${idx}`}
                                            virtualIndex={idx + 1}
                                            className={calcCardStyles(styles.slide, true)}
                                        >
                                            {() => {
                                                return (
                                                    <div ref={cardRef} className={calcCardStyles(styles.carouselCard, true)}>
                                                        <TariffCardLoader />
                                                    </div>
                                                );
                                            }}
                                        </SwiperSlide>
                                    ))}
                            </Swiper>
                        </div>
                    )}
                </div>
            </div>
        );
    }
);

TariffsList.displayName = 'TariffsList';
